import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Overlays render into <body> (never boxed into a transformed parent); inline when
// there is no document (server render in tests).
const toBody = (node) => (typeof document === 'undefined' ? node : createPortal(node, document.body))
import { X } from 'lucide-react'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import RecordMeta from '../components/RecordMeta.jsx'
import { Button } from './Button.jsx'

/**
 * Drawer (right-hand side panel) and Modal (centred) — ONE shell each.
 *
 * Audit: 77 hand-built overlays. 41 had no Escape; most discarded unsaved edits
 * on a stray backdrop click; backdrops, widths, z-index and close buttons all
 * differed. The behaviour below is the rule, built in so pages can't drift:
 *
 *   Esc / backdrop / ✕  → close
 *   busy  (saving)      → none of them close; ✕ is disabled
 *   dirty (unsaved)     → they ask "ทิ้งการแก้ไข?" first (ConfirmDialog)
 *   stacked overlays    → Esc only closes the TOP one
 *
 * Use a Drawer to view / edit ONE record. Use a Modal for a short, focused
 * question or form (≤ ~5 fields) that is not a record. Use ConfirmDialog for
 * yes/no.
 */

// Esc reaches only the top-most modal: a ConfirmDialog opened over a Drawer
// must close itself, not the drawer underneath. Every overlay here and the
// existing ConfirmDialog mark themselves aria-modal, and document order puts
// the newest last.
function useTopEscape(rootRef, enabled, onEscape) {
  const cb = useRef(onEscape)
  cb.current = onEscape
  useEffect(() => {
    if (!enabled) return undefined
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      const all = document.querySelectorAll('[aria-modal="true"]')
      if (all.length && all[all.length - 1] !== rootRef.current) return
      cb.current?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, rootRef])
}

function useGuardedClose({ open, busy, dirty, onClose }) {
  const rootRef = useRef(null)
  const [asking, setAsking] = useState(false)
  useEffect(() => { if (!open) setAsking(false) }, [open])
  const requestClose = () => {
    if (busy) return
    if (dirty) { setAsking(true); return }
    onClose?.()
  }
  useTopEscape(rootRef, open && !asking, requestClose)
  return { rootRef, asking, setAsking, requestClose }
}

function DiscardConfirm({ message, onDiscard, onKeep }) {
  return (
    <ConfirmDialog
      tone="danger"
      title="ทิ้งการแก้ไข?"
      message={message || 'ยังไม่ได้บันทึก — การแก้ไขในหน้านี้จะหายไป'}
      confirmLabel="ทิ้งการแก้ไข"
      cancelLabel="กลับไปแก้ไข"
      onConfirm={onDiscard}
      onCancel={onKeep}
    />
  )
}

const DRAWER_W = { sm: 'max-w-[480px]', md: 'max-w-[560px]', lg: 'max-w-[680px]', xl: 'max-w-[960px]' }

/**
 *   open, onClose            controlled
 *   title, subtitle, icon    header (title 16px)
 *   headerActions            buttons left of ✕ (e.g. "แก้ไข", ‹ › record stepping)
 *   footer                   usually <DrawerFooter>; pinned to the bottom
 *   width                    'sm' 480 · 'md' 560 (default) · 'lg' 680 · 'xl' 960
 *   busy / dirty             see the rules above; discardMessage overrides the text
 *   record                   the STORED row (existing data). Pins its audit line to the
 *                            bottom-left corner: "#id · สร้าง <who> <when> · แก้ไข <who> <when>"
 *                            (components/RecordMeta — it knows every table's audit column
 *                            names, and hides แก้ไข when the row was never really edited).
 *                            Leave it out on a create form: nothing is shown.
 *   recordId / recordTable   when the primary key is not `id` / the id's tooltip
 */
export function Drawer({ open = true, onClose, title, subtitle, icon: Icon, headerActions, footer, record, recordId, recordTable, width = 'md', busy = false, dirty = false, discardMessage, children }) {
  const { rootRef, asking, setAsking, requestClose } = useGuardedClose({ open, busy, dirty, onClose })
  if (!open) return null
  // Portalled to <body>: a Drawer opened from inside another Drawer (whose slide-in
  // animation leaves a transform on its panel) would otherwise be boxed into it.
  return toBody(
    <div ref={rootRef} className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={requestClose} />
      <div className={`relative h-full w-full ${DRAWER_W[width] || DRAWER_W.md} bg-slate-50 shadow-2xl shadow-slate-900/20 flex flex-col animate-ss-slide-in`}>
        <div className="flex items-start gap-3 bg-white border-b border-slate-200 px-5 py-3.5">
          {Icon && (
            <span className="flex-none w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
              <Icon size={17} strokeWidth={2} />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold text-slate-900 leading-snug break-words">{title}</div>
            {subtitle && <div className="text-[13px] text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          {headerActions && <div className="flex items-center gap-1.5">{headerActions}</div>}
          <button type="button" onClick={requestClose} disabled={busy} title="ปิด (Esc)" aria-label="ปิด"
            className="flex-none w-8 h-8 -mr-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center disabled:opacity-40">
            <X size={17} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {(footer || record) && (
          <div className="bg-white border-t border-slate-200 px-5 py-3">
            {footer}
            {record && <RecordMeta row={record} id={recordId} table={recordTable} className={footer ? 'mt-2' : ''} />}
          </div>
        )}
      </div>
      {asking && <DiscardConfirm message={discardMessage} onDiscard={() => { setAsking(false); onClose?.() }} onKeep={() => setAsking(false)} />}
    </div>,
  )
}

/**
 * The standard footer: [danger action] · [status text] ……… [ยกเลิก] [บันทึก]
 *   status       why Save is blocked, or progress ("กำลังบันทึก 2/5") — shown left of the buttons
 *   statusTone   'muted' | 'warn' | 'error'
 *   danger       e.g. <Button variant="danger-soft" icon={Trash2}>ลบ</Button> — far left
 *   secondary    a second, NON-destructive action after it (คืนค่าเดิม, ใช้ค่าล่าสุด, สร้างการรักษา)
 *   canSave      false → Save disabled (say why in `status`)
 *   saveLabel    default 'บันทึก'; say what it does when it is more than a save ("ออกหนังสือเตือน")
 *   hideCancel   read-only views: the ✕ is enough
 */
export function DrawerFooter({ onCancel, onSave, saving = false, canSave = true, saveLabel = 'บันทึก', cancelLabel = 'ยกเลิก', status, statusTone = 'muted', danger, secondary, hideCancel = false }) {
  const tone = statusTone === 'error' ? 'text-rose-600' : statusTone === 'warn' ? 'text-amber-700' : 'text-slate-500'
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {danger}
      {secondary}
      <div className={`flex-1 min-w-0 text-[12.5px] ${tone}`}>{status}</div>
      {!hideCancel && <Button variant="secondary" onClick={onCancel} disabled={saving}>{cancelLabel}</Button>}
      {onSave && <Button onClick={onSave} loading={saving} disabled={!canSave}>{saveLabel}</Button>}
    </div>
  )
}

const MODAL_W = { sm: 'max-w-[400px]', md: 'max-w-[520px]', lg: 'max-w-[720px]' }

const ICON_TONE = {
  brand: 'bg-brand-50 text-brand-600', amber: 'bg-amber-50 text-amber-600',
  rose: 'bg-rose-50 text-rose-600', emerald: 'bg-emerald-50 text-emerald-600', slate: 'bg-slate-100 text-slate-500',
}

/**
 * Centred dialog. Same rules as Drawer. width 'sm' 400 · 'md' 520 (default) · 'lg' 720.
 * icon / iconTone ('brand' | 'amber' | 'rose' | 'emerald' | 'slate') — a tile left of the title.
 */
export function Modal({ open = true, onClose, title, subtitle, icon: Icon, iconTone = 'brand', footer, width = 'md', busy = false, dirty = false, discardMessage, children }) {
  const { rootRef, asking, setAsking, requestClose } = useGuardedClose({ open, busy, dirty, onClose })
  if (!open) return null
  return toBody(
    <div ref={rootRef} className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={requestClose} />
      <div className={`relative w-full ${MODAL_W[width] || MODAL_W.md} max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 animate-ss-pop-in`}>
        <div className="flex items-start gap-3 px-5 pt-4 pb-3">
          {Icon && (
            <span className={`flex-none w-9 h-9 rounded-xl flex items-center justify-center ${ICON_TONE[iconTone] || ICON_TONE.brand}`}>
              <Icon size={17} strokeWidth={2} />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold text-slate-900 leading-snug">{title}</div>
            {subtitle && <div className="text-[13px] text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={requestClose} disabled={busy} title="ปิด (Esc)" aria-label="ปิด"
            className="flex-none w-8 h-8 -mr-1.5 -mt-0.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center disabled:opacity-40">
            <X size={17} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && <div className="border-t border-slate-100 px-5 py-3">{footer}</div>}
      </div>
      {asking && <DiscardConfirm message={discardMessage} onDiscard={() => { setAsking(false); onClose?.() }} onKeep={() => setAsking(false)} />}
    </div>,
  )
}

/** Re-exported so pages import every overlay from the kit. Yes/no only — always says what will be lost. */
export { ConfirmDialog }
